import React, { useContext, useMemo, useCallback, useEffect, Fragment } from 'react';
import { PatientChartContext } from '../../../contexts/patientChart';
import { RESPONSE_POPUP_KEY, MODAL_KEY, CONFIRMATION_POPUP_KEY } from '../../../constants/patientChart.constant';
import { POPUP_TYPE } from '../../subComponents/popup';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import PPButton from '../../subComponents/button';

export const CallPatient = ({
  calling = {},
  orderId = '',
  doctorId = '',
  callSummaryReasons = [],
  callPatientInfo = {},
  callPatient = async () => {},
  saveCallSummary = async () => {},
  getNotesTabInfo = async () => {},
}) => {
  const {
    call,
    setCall,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);
  const { loading } = callPatientInfo.api;

  const reasonCollection = useMemo(() => {
    const collection = new Map();
    for (let i = 0; i < callSummaryReasons.length; i++) {
      const reason = callSummaryReasons[i] || { value: -1, display: '' };
      collection.set(reason.value, reason);
    }
    return collection;
  }, [callSummaryReasons]);

  const handleCallingResponse = useCallback(() => {
    const _call = { ...call };

    if (calling && calling.DoctorNumber) {
      _call.doctorNumber = calling.DoctorNumber;
    }

    if (_call.doctorNumber && _call.doctorNumber.length !== 10) {
      _call.error.set('doctorNumber', 'Phone number must be 10 digits, no spaces (e.g.: 9995551212)');
    }

    setCall(_call);
  }, [call, calling]);

  useEffect(handleCallingResponse, [calling]);

  const handleDoctorNumberChange = ({ target }) => {
    const error = new Map(call.error);
    error.delete('doctorNumber');

    if (target.value.length !== 10) {
      error.set('doctorNumber', 'Phone number must be 10 digits, no spaces (e.g.: 9995551212)');
    }
    setCall({ ...call, doctorNumber: target.value, error });
  };

  const handleReasonChange = ({ target }) => {
    const reason = reasonCollection.get(target.value) || { value: -1, display: '' };
    setCall({ ...call, reason: reason.value });
  };

  const handleCallPatient = async ({ key }) => {
    const body = {
      DoctorNumber: call.doctorNumber,
      OrderId: orderId,
      StorefrontName: calling.StorefrontName,
      StoreFrontNumber: calling.CustomerServiceNumber,
    };

    try {
      const { status, data } = await callPatient(body);
      if (status === 200) {
        const { Data: { roomId } } = data;
        setCall({ ...call, roomId });
        handleMultipleModalClose.call(this, { keys: [key] });

        // reload the notes
        getNotesTabInfo();
      }

      if (status !== 200) {
        const { Error: error } = data;
        let heading = 'Error';

        if (typeof error === 'string' && error.toLowerCase().includes('twilio')) {
          heading = 'Twilio Error';
        }

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.CALL_PATIENT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
    }
  };

  const handleSubmitCallSummary = async ({ key }) => {
    const body = {
      OrderId: orderId,
      DoctorId: doctorId,
      CallSummaryId: call.reason,
      CallPatient: 1,
      RoomId: call.roomId,
    };

    try {
      saveCallSummary(body).then(() => {}).catch(() => {}).finally(() => {
        setCall({ ...call, error: new Map(), reason: -1, roomId: '' });
      });
    } catch (e) {

    }
    handleMultipleModalClose.call(this, { keys: [key] });
  };

  const _disabled = useMemo(() => {
    return loading || call.error.has('doctorNumber');
  }, [loading, call]);

  const InputsWithRoomId = (
    <>
      <div className='flex align-items-center m-b-2'>
        <label className='input-label color-primary'>Call initiated to your phone number listed above...</label>
      </div>

      <div className='flex align-items-center'>
        <label className="input-label width-100 m-r-4">Call Summary:</label>
        <Select
          id='reasonId'
          labelId="reasonId"
          value={call.reason}
          onChange={handleReasonChange}
          items={callSummaryReasons}
        />
      </div>
    </>
  );
  const InputsCollection = new Map([
    [false, <Fragment />],
    [true, InputsWithRoomId],
  ]);

  const Template = (
    <div className='flex flex-column flex-fill m-b-5'>
      <div className='flex align-items-center m-b-5'>
        <p className='m-0'>
          Click the button below to initiate a call to the patient.
        </p>
      </div>

      <div className='flex align-items-center m-b-10'>
        <Input
          name='doctorNumber'
          type='text'
          label='Your Phone Number:'
          max={10}
          className='flex flex-fill'
          labelClassName='width-140'
          value={call.doctorNumber}
          error={call.error.has('doctorNumber')}
          message={
            call.error.get('doctorNumber') || '10-digit phone number to be called to connect with the patient. Enter without spaces (e.g. 4807075555).'
          }
          onChange={handleDoctorNumberChange}
        />
      </div>

      <div className='flex flex-column align-items-flex-start m-b-5'>
        <label className="input-label m-b-2">Storefront:</label>
        <p className='m-0 font-22 bold'>{calling.StorefrontName}</p>
      </div>

      <div className='flex flex-column align-items-flex-start m-b-5'>
        <label className="input-label m-b-2">Patient Support Phone Number:</label>
        <p className='m-0 font-22 bold'>{calling.CustomerServiceNumber}</p>
      </div>

      {InputsCollection.get(!!call.roomId)}
    </div>
  );

  const ActionsWithoutRoomId = (
    <div className='flex justify-content-flex-end'>
      <PPButton label='Call' variant='contained' loading={loading} disabled={_disabled} endGutter onClick={handleCallPatient.bind(this, { key: CONFIRMATION_POPUP_KEY.CALL_PATIENT, modal: {} })} />
      <PPButton
        label='Cancel'
        variant='cancel'
        disabled={loading}
        onClick={handleMultipleModalClose.bind(this, { keys: [ MODAL_KEY.CALL_PATIENT, CONFIRMATION_POPUP_KEY.CALL_PATIENT ] })}
      />
    </div>
  );
  const ActionsWithRoomId = (
    <div className='flex justify-content-flex-end'>
      <PPButton
        label='Close'
        variant='contained'
        disabled={call.reason === -1}
        onClick={handleSubmitCallSummary.bind(this, { key: MODAL_KEY.CALL_PATIENT, modal: {} })}
      />
    </div>
  );
  const ActionsCollection = new Map([
    [false, ActionsWithoutRoomId],
    [true, ActionsWithRoomId],
  ]);

  return (
    <div className='flex flex-column flex-fill m-5 patient-chart-container'>
      {Template}
      {ActionsCollection.get(!!call.roomId)}
    </div>
  );
};

export default CallPatient;
